/**
 * PR-28A — "Pantalla Limpia Temporal" (replaces the old Focus Mode).
 *
 * Time-boxed clean screen. While active, persistent floating overlays are
 * minimized. When the timer expires EVERYTHING comes back automatically;
 * there is no permanent user-side suppression.
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export const CLEAN_SCREEN_DURATION_OPTIONS_MS = [30_000, 60_000, 120_000];
const CONFIG_KEY = "vs:pr28a_clean_screen_default_ms";

interface CleanState {
  activeUntil: number; // 0 = inactive
  defaultMs: number;
}

function readDefaultMs(): number {
  if (typeof window === "undefined") return 60_000;
  try {
    const v = window.localStorage.getItem(CONFIG_KEY);
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) && n >= 15_000 ? n : 60_000;
  } catch { return 60_000; }
}

let state: CleanState = { activeUntil: 0, defaultMs: readDefaultMs() };
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => state;
const emit = () => listeners.forEach((l) => l());

function tick() {
  if (state.activeUntil > 0 && Date.now() >= state.activeUntil) {
    void trackEvent("clean_screen_mode_expired", { at: Date.now() });
    state = { ...state, activeUntil: 0 };
    emit();
  }
}
if (typeof window !== "undefined") {
  window.setInterval(tick, 1000);
}

export function activateCleanScreen(ttlMs?: number, source: string = "user"): number {
  const ttl = Math.max(15_000, ttlMs ?? state.defaultMs);
  const until = Date.now() + ttl;
  state = { ...state, activeUntil: until };
  void trackEvent("clean_screen_mode_activated", { ttl_ms: ttl, source });
  emit();
  return until;
}

export function deactivateCleanScreen(): void {
  if (!state.activeUntil) return;
  void trackEvent("clean_screen_mode_expired", { at: Date.now(), source: "manual" });
  state = { ...state, activeUntil: 0 };
  emit();
}

export function setDefaultCleanScreenMs(ms: number): void {
  const clamped = Math.max(15_000, Math.min(10 * 60_000, ms));
  state = { ...state, defaultMs: clamped };
  if (typeof window !== "undefined") window.localStorage.setItem(CONFIG_KEY, String(clamped));
  emit();
}

export function useCleanScreen() {
  const s = useSyncExternalStore(subscribe, snap, snap);
  const remainingMs = s.activeUntil ? Math.max(0, s.activeUntil - Date.now()) : 0;
  return { active: remainingMs > 0, remainingMs, defaultMs: s.defaultMs, activeUntil: s.activeUntil };
}
